/**
 * Why the bank returned a payment from a bank file (CUR-2812; the returns import, CUR-2815): the direct
 * entry return codes as ANZ Transactive Global File Formats (Aug 2026) section 8.7.4 lists them, with no
 * 007. Other banks' lists are not checked yet. The keys are what a bank file line records; a returns file
 * carries the three-digit code.
 */
export const BANK_RETURN_REASONS = {
    INVALID_BSB: { code: '001', label: 'Invalid BSB number' },
    PAYMENT_STOPPED: { code: '002', label: 'Payment stopped' },
    ACCOUNT_CLOSED: { code: '003', label: 'Account closed' },
    CUSTOMER_DECEASED: { code: '004', label: 'Customer deceased' },
    NO_OR_INCORRECT_ACCOUNT: { code: '005', label: 'No account or incorrect account number' },
    REFER_TO_CUSTOMER: { code: '006', label: 'Refer to customer' },
    INVALID_USER_ID: { code: '008', label: 'Invalid user ID number' },
    TECHNICALLY_INVALID: { code: '009', label: 'Technically invalid' },
} as const satisfies Record<string, { code: string; label: string }>;

export type BankReturnReason = keyof typeof BANK_RETURN_REASONS;

/**
 * Default-off rollout gate for bank files (CUR-2807, decision V1). Organisation-scoped like the payment
 * approval flags, and needs both of them: bank files are on for an organisation when this flag and both
 * payment approval flags are on and its bank account is saved (BF-A3). The server reads it failing closed.
 */
export const ENABLE_CLAIM_BANK_FILES_FLAG = 'enable-claim-bank-files';

/**
 * The organisation's bank account (CUR-2808, BF-A1): each field's limit as a bank file carries it (ANZ
 * Transactive Global File Formats, section 2.1). The account name is the funding account's title, 32
 * characters; the BSB, user ID and bank code are exact lengths.
 */
export const ORG_BANK_ACCOUNT_LIMITS = {
    accountName: 32,
    bsb: 6,
    accountNo: 9,
    bankCode: 3,
    directEntryUserId: 6,
    userName: 26,
    remitterName: 16,
    entryDescription: 12,
} as const satisfies Record<string, number>;

/** Where a bank file names the funding account (BF-A1): its first line, or a balancing line at the end. */
export const ORG_BANK_ACCOUNT_FUNDING_PLACEMENTS = ['HEADER', 'BALANCING_LINE'] as const;

export type OrgBankAccountFundingPlacement = (typeof ORG_BANK_ACCOUNT_FUNDING_PLACEMENTS)[number];

export const ORG_BANK_ACCOUNT_FUNDING_PLACEMENT_LABELS = {
    HEADER: 'In the file header',
    BALANCING_LINE: 'Balancing line',
} as const satisfies Record<OrgBankAccountFundingPlacement, string>;

/**
 * Text a bank file's alphanumeric fields accept (section 2.1.1): letters, digits, space and this
 * punctuation. The renderer (`cleanAbaText`, server) drops anything else; the bank account's text fields
 * are refused instead, so what is saved is exactly what a file carries.
 */
export const ABA_TEXT_PATTERN = /^[A-Za-z0-9 &',\-./+$!%()*#=:?[\]_^@]*$/;

/** The punctuation `ABA_TEXT_PATTERN` allows, as a message lists it. */
export const ABA_TEXT_PUNCTUATION = "& ' , - . / + $ ! % ( ) * # = : ? [ ] _ ^ @";

/**
 * The longest claim number bank files can carry (decision V18): a line's reference is the claim number,
 * a hyphen and the payment's number on the claim (up to three digits), in the bank's 18 characters.
 */
export const BANK_FILE_REFERENCE_MAX_LENGTH = 18;
export const BANK_FILE_PAYMENT_NUMBER_SUFFIX = '-999';
